import { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LayoutDashboard, Users, ShoppingCart, Package, Receipt, BarChart3, Layers, User, Box, Clock, Bell, MonitorPlay, Sparkles, GitBranch, FolderTree, Percent, Boxes, Wrench, Truck, ArrowLeftRight, Store, Map, Warehouse, PackageOpen, BadgeIndianRupee, Plus, ClipboardCheck, Ruler, AlertTriangle, History, Printer, MessageSquare, CalendarDays, Gift, Monitor, Scan, Tag, KeyRound } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { FloatingNav } from '../components/FloatingNav';
import { Sidebar } from '../components/Sidebar';
import { TopBar } from '../components/TopBar';
import { wsManager } from '../utils/websocket';
import { OrgAdminDashboard } from './org/OrgAdminDashboard';
import { OrgUsersList } from './org/OrgUsersList';
import { RolesAclTab } from './org/RolesAclTab';
import {
  catalogProductsOf, stockRowsOf, resolveStockScopes, effectiveBranchesOf, CENTRAL_LOCATION,
  emergencyRequestsOf, locationLabelOf, branchesOf
} from '../data/store';
import { PurchasesTab } from './org/PurchasesTab';
import { InventoryPendingTab } from './org/InventoryPendingTab';
import { InventoryActiveTab } from './org/InventoryActiveTab';
import { InventoryHistoryTab } from './org/InventoryHistoryTab';
import { InventoryStatusTab } from './org/InventoryStatusTab';
import { BillingTab } from './org/BillingTab';
import { SalesLedgerTab } from './org/SalesLedgerTab';
import { PCategoryTab } from './org/PCategoryTab';
import { PSubCategoryTab } from './org/PSubCategoryTab';
import { ProductsTab } from './org/ProductsTab';
import { SCategoryTab } from './org/SCategoryTab';
import { SSubCategoryTab } from './org/SSubCategoryTab';
import { ServicesTab } from './org/ServicesTab';
import { UnitsTab } from './org/UnitsTab';
import { GstTab } from './org/GstTab';
import { SuppliersTab } from './org/SuppliersTab';
import { OrgAdminProfile } from './org/OrgAdminProfile';
import { BranchesTab } from './org/BranchesTab';
import { ZonesTab } from './org/ZonesTab';
import { WarehousesTab } from './org/WarehousesTab';
import { MyBranchTab } from './org/MyBranchTab';
import { WarehouseDashboard } from './org/WarehouseDashboard';
import { WhPurchasesTab } from './org/WhPurchasesTab';
import { WhSourcingTab } from './org/WhSourcingTab';
import { WhStockTab } from './org/WhStockTab';
import { OrgWhPurchasesTab } from './org/OrgWhPurchasesTab';
import { OrgWhStockTab } from './org/OrgWhStockTab';
import { OrgWhReceivedTab } from './org/OrgWhReceivedTab';
import { OrgWhTransportTab } from './org/OrgWhTransportTab';
import { BranchStocksTab } from './org/BranchStocksTab';
import { EmergencyRequestsView } from './org/EmergencyRequestsView';
import { WhInventoryHistoryTab } from './org/WhInventoryHistoryTab';
import { WhPendingStockTab } from './org/WhPendingStockTab';
import { WhInventoryStatusTab } from './org/WhInventoryStatusTab';
import { BranchReceivedDetailsTab } from './org/BranchReceivedDetailsTab';
import { DeliveryTab } from './org/DeliveryTab';
import { TransportTab } from './org/TransportTab';
import { EcommerceTab } from './org/EcommerceTab';
import { SubscriptionTab } from './org/SubscriptionTab';
import { PrintBillModal } from '../components/PrintBillModal';
import { SalesReportTab } from './org/SalesReportTab';
import { PurchaseReportTab } from './org/PurchaseReportTab';
import { OrgValidationSettingsTab } from './org/OrgValidationSettingsTab';
import { DeliveryFeesTab } from './org/DeliveryFeesTab';
import { OffersDiscountsTab } from './org/OffersDiscountsTab';
import { CustomerFeedbackTab } from './org/CustomerFeedbackTab';
import { AppointmentsTab } from './org/AppointmentsTab';
import { MyBookingsTab } from './org/MyBookingsTab';
import { StaffScheduleTab } from './org/StaffScheduleTab';
import { NotificationSettingsTab } from './org/NotificationSettingsTab';
import { KioskQueueTab } from './org/KioskQueueTab';
import { CustomersTab } from './org/CustomersTab';
import { MembershipsTab } from './org/MembershipsTab';
import { MasterPackagesTab } from './org/MasterPackagesTab';
import { MasterShiftsTab } from './org/MasterShiftsTab';
import { PrinterTab } from './org/PrinterTab';
import { ScannerTab } from './org/ScannerTab';
import { BarcodeLabelsTab } from './org/BarcodeLabelsTab';
import { useSharedStore } from '../hooks/useSharedStore';
import { can, canAny, loadMyAccess } from '../data/accessStore';
import { ORG_TYPES, ROLES, getOrgSettings, mainBranchOf, isModuleOn, loadAllTenantData, findOrgById, daysUntilExpiry } from '../data/store';

// How often the low-stock check re-runs. Shorter than any Alert Interval option
// on purpose: the per-product cooldown enforces the user's chosen interval, so a
// short tick means a product that just hit zero is reported promptly.
const ALERT_TICK_MS = 30 * 1000;

const buildNav = (role, orgType, flags, isT0, isT1) => {
  const isOrgAdmin = role === ROLES.ORG_ADMIN;
  const isBranchAdmin = role === ROLES.BRANCH_ADMIN;
  const isWarehouseAdmin = role === ROLES.WAREHOUSE_ADMIN;
  const isWarehouseUser = role === ROLES.WAREHOUSE_USER;
  const {
    zoneEnabled, warehouseEnabled, branchesEnabled,
    usersEnabled, inventoryEnabled, purchaseEnabled,
    deliveryEnabled, transportEnabled, ecommerceEnabled,
  } = flags;

  // Master (product/service catalog + GST) is shared by org, branch, and
  // warehouse admins — built once up front so the warehouse early-return can
  // include it too.
  const masterChildren = [];
  if (orgType === ORG_TYPES.PRODUCT || orgType === ORG_TYPES.BOTH) {
    if (can('master.product_categories', 'edit')) {
      masterChildren.push({ id: 'master-p-category', label: 'P-Category', icon: FolderTree });
      masterChildren.push({ id: 'master-p-subcategory', label: 'P-Sub-Category', icon: Layers });
    }
    if (can('master.products', 'edit')) {
      masterChildren.push({ id: 'master-products', label: 'Products', icon: Boxes });
    }
  }
  if (orgType === ORG_TYPES.SERVICE || orgType === ORG_TYPES.BOTH) {
    if (can('master.service_categories', 'edit')) {
      masterChildren.push({ id: 'master-s-category', label: 'S-Category', icon: FolderTree });
      masterChildren.push({ id: 'master-s-subcategory', label: 'S-Sub-Category', icon: Layers });
    }
    if (can('master.services', 'edit')) {
      masterChildren.push({ id: 'master-services', label: 'Services', icon: Wrench });
    }
  }
  const hideAdvancedMaster = isT0 || isT1;
  const isTier2 = purchaseEnabled && !deliveryEnabled && !transportEnabled && !warehouseEnabled;
  const hideDeliveryFees = hideAdvancedMaster || isTier2;

  if (!hideAdvancedMaster && can('master.units', 'edit')) {
    masterChildren.push({ id: 'master-units', label: 'Unit Hierarchy', icon: Ruler });
  }
  if (can('master.gst', 'edit')) {
    masterChildren.push({ id: 'master-gst', label: 'GST', icon: Percent });
  }
  if (!hideAdvancedMaster && can('master.suppliers', 'edit')) {
    masterChildren.push({ id: 'master-suppliers', label: 'Suppliers', icon: Truck });
  }
  if (!hideDeliveryFees && !isWarehouseAdmin && can('ecommerce.fees', 'edit')) {
    masterChildren.push({ id: 'master-delivery-fees', label: 'Delivery Fees', icon: Truck });
  }


  // Warehouse Admins get a focused sidebar — their warehouse views plus the
  // shared product Master.
  // Warehouse staff get a focused sidebar.
  // Warehouse Admins get everything; Warehouse Users get a restricted view.
  if (isWarehouseAdmin || isWarehouseUser) {
    const whInventoryChildren = [];
    if (isWarehouseAdmin) {
      whInventoryChildren.push({ id: 'wh-purchases', label: 'Purchases', icon: ShoppingCart });
    }
    whInventoryChildren.push({ id: 'wh-pending-stock', label: 'Pending Stock', icon: Clock });
    whInventoryChildren.push({ id: 'wh-stock', label: 'Active Stock', icon: PackageOpen });
    whInventoryChildren.push({ id: 'barcode-labels', label: 'Barcode & Labels', icon: Tag });
    if (isWarehouseAdmin) {
      whInventoryChildren.push({ id: 'wh-inventory-history', label: 'History', icon: History });
      whInventoryChildren.push({ id: 'wh-inventory-status', label: 'Status', icon: AlertTriangle });
    }

    const whNav = [
      { id: 'dashboard', label: 'My Warehouse', icon: Warehouse },
      { id: 'wh-inventory', label: 'Inventory', icon: Package, children: whInventoryChildren },
      { id: 'wh-transport', label: 'Transport', icon: ArrowLeftRight },
    ];
    if (isWarehouseAdmin) {
      whNav.splice(2, 0, { id: 'wh-sourcing', label: 'Branch Sourcing', icon: GitBranch });
      if (flags.emergencyEnabled) {
        whNav.splice(4, 0, { id: 'emergency', label: 'Emerg Product', icon: AlertTriangle });
      }
      if (masterChildren.length) {
        whNav.push({ id: 'master', label: 'Master', icon: Layers, children: masterChildren });
      }
    }
    whNav.push({ id: 'profile', label: 'My Profile', icon: User });
    return whNav;
  }

  // Inventory sub-menu order: Purchase → Pending → Active. "Purchase" is the
  // former top-level Purchases item, now folded in (only when purchase is on).
  const inventoryChildren = [];
  if (purchaseEnabled) inventoryChildren.push({ id: 'purchases', label: 'Purchase', icon: ShoppingCart });
  inventoryChildren.push({ id: 'inventory-pending', label: 'Pending', icon: Clock });
  inventoryChildren.push({ id: 'inventory-active', label: 'Active', icon: Box });
  inventoryChildren.push({ id: 'barcode-labels', label: 'Barcode & Labels', icon: Tag });
  inventoryChildren.push({ id: 'inventory-history', label: 'History', icon: History });
  inventoryChildren.push({ id: 'inventory-status', label: 'Status', icon: AlertTriangle });

  const base = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  ];

  if (zoneEnabled && can('zones', 'edit')) base.push({ id: 'zones', label: 'Zones', icon: Map });

  // Org admin: grouped Warehouse menu spanning ALL warehouses (view-only).
  if (isOrgAdmin && warehouseEnabled && can('warehouse.locations')) {
    base.push({
      id: 'org-warehouse',
      label: 'Warehouse',
      icon: Warehouse,
      children: [
        { id: 'warehouses', label: 'Add', icon: Plus },
        { id: 'org-wh-purchases', label: 'Purchases', icon: ShoppingCart },
        { id: 'org-wh-stock', label: 'All Stocks', icon: PackageOpen },
        { id: 'org-wh-received', label: 'Received', icon: Package },
        { id: 'org-wh-transport', label: 'Transport', icon: ArrowLeftRight },
      ],
    });
  }

  // Branch admin: grouped Warehouse menu
  if (isBranchAdmin && warehouseEnabled && can('warehouse.locations')) {
    base.push({
      id: 'branch-warehouse',
      label: 'Warehouse',
      icon: Warehouse,
      children: [
        { id: 'branch-stocks', label: 'Stocks', icon: PackageOpen },
        { id: 'branch-received-details', label: 'Received Details', icon: Package },
      ],
    });
  }

  if (branchesEnabled && can('branches', 'edit')) base.push({ id: 'branches', label: 'Branches', icon: GitBranch });
  // Users and Roles sit together: one says who works here, the other says what
  // they may do. Both keyed off edit, not view — the till reads the staff list
  // to put a name on a bill without being shown the screen that changes it.
  //
  // Both gate on the Users module too. Only Staff did, so a shop on a plan
  // without it got no Staff screen and a Roles & Access screen anyway — and
  // because it was then the only child, it was promoted to a top-level item.
  // A shop that cannot see its staff has no use for the screen that says what
  // its staff may do.
  const peopleChildren = [];
  if (usersEnabled && can('users', 'edit')) peopleChildren.push({ id: 'users', label: 'Staff', icon: Users });
  if (usersEnabled && can('roles', 'edit')) peopleChildren.push({ id: 'roles-acl', label: 'Roles & Access', icon: KeyRound });
  if (peopleChildren.length === 1) base.push({ ...peopleChildren[0], label: peopleChildren[0].id === 'users' ? 'Users' : 'Roles & Access' });
  else if (peopleChildren.length > 1) base.push({ id: 'people', label: 'Users', icon: Users, children: peopleChildren });
  if (inventoryEnabled && can('inventory.stock')) base.push({ id: 'inventory', label: 'Inventory', icon: Package, children: inventoryChildren });
  if (can('billing')) base.push({ id: 'billing', label: 'Billing', icon: Receipt });
  if (can('sales')) base.push({ id: 'sales', label: orgType === ORG_TYPES.SERVICE ? 'Invoices' : 'Sales', icon: BarChart3 });
  if (flags.feedbackEnabled && can('feedback')) {
    base.push({ id: 'feedback', label: 'Feedback', icon: MessageSquare });
  }
  if (flags.appointmentsEnabled && can('appointments')) {
    base.push({
      id: 'appointments-master',
      label: 'Appointments',
      icon: CalendarDays,
      children: [
        { id: 'appointments', label: 'Appointment Booking', icon: CalendarDays },
        { id: 'my-bookings', label: 'My Booking', icon: CalendarDays },
        { id: 'staff-schedule', label: 'Schedule', icon: Clock },
        { id: 'kiosk-queue', label: 'TV Queue Display', icon: MonitorPlay },
        { id: 'notifications', label: 'Notifications', icon: Bell },
      ],
    });
  }
  if (flags.customerEnabled && can('customers')) {
    base.push({ id: 'customer-management', label: 'Customer', icon: Users });
  }

  if (flags.membershipsEnabled && can('memberships')) {
    base.push({ id: 'customer-packages', label: 'Membership', icon: Gift });
  }

  if (flags.offersDiscountsEnabled && can('offers')) {
    base.push({ id: 'offers-discounts', label: 'Offers & Discounts', icon: Percent });
  }

  if (orgType === ORG_TYPES.PRODUCT || orgType === ORG_TYPES.BOTH) {
    if (deliveryEnabled && can('delivery')) base.push({ id: 'delivery', label: 'Delivery', icon: Truck });
    if (transportEnabled && can('transport')) base.push({ id: 'transport', label: 'Transport', icon: ArrowLeftRight });
    if (ecommerceEnabled && can('ecommerce.orders')) base.push({ id: 'ecommerce', label: 'Online Store', icon: Store });
  }

  if (flags.emergencyEnabled && can('emergency')) {
    base.push({ id: 'emergency', label: 'Emerg Product', icon: AlertTriangle });
  }

  // Master is shared by org admin and branch admin (warehouse admin gets it in
  // its own focused sidebar above).
  if (masterChildren.length) {
    base.push({ id: 'master', label: 'Master', icon: Layers, children: masterChildren });
  }
  // Reports group — whoever the role lets read a report
  if (flags.reportsEnabled && canAny(['reports.sales', 'reports.purchase'])) {
    const reportChildren = [];
    if (can('reports.sales')) {
      reportChildren.push({ id: 'report-sales', label: orgType === ORG_TYPES.SERVICE ? 'Booking Report' : 'Sales Report', icon: Receipt });
    }
    if (orgType !== ORG_TYPES.SERVICE && can('reports.purchase')) {
      reportChildren.push({ id: 'report-purchase', label: 'Purchase Report', icon: ShoppingCart });
    }

    base.push({
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      children: reportChildren,
    });
  }

  // Devices group
  const deviceChildren = [];
  // View, not edit, unlike Master above. Pairing the counter's Bluetooth
  // printer is daily work for whoever is on the till, not administration —
  // gating it on edit would take the Printer screen away from the one person
  // who needs it when the printer drops off.
  if (flags.printerEnabled && can('devices.printer')) deviceChildren.push({ id: 'device-printer', label: 'Printer', icon: Printer });
  if (flags.scannerEnabled && can('devices.scanner')) deviceChildren.push({ id: 'device-scanner', label: 'Scanner', icon: Scan });
  if (deviceChildren.length > 0) {
    base.push({
      id: 'devices',
      label: 'Devices',
      icon: Monitor,
      children: deviceChildren,
    });
  }

  if (isBranchAdmin && branchesEnabled) base.push({ id: 'mybranch', label: 'My Branch', icon: GitBranch });
  base.push({ id: 'profile', label: 'My Profile', icon: User });
  return base;
};

const flatten = (items) => {
  const out = [];
  items.forEach((i) => {
    out.push(i);
    if (i.children) i.children.forEach((c) => out.push(c));
  });
  return out;
};

export const OrgDashboard = ({
  activeTab,
  setActiveTab,
  handleLogout,
  showNotification,
  navMode,
  setNavMode,
  currentUser,
  currentOrg,
  role,
  onUpdateUser,
  onUpdateOrg,
}) => {
  const { version: dataVersion, loading: isStoreLoading } = useSharedStore();
  const [billingPrefill, setBillingPrefill] = useState(null);

  const actualRole = role || ROLES.ORG_USER;
  const isAdmin = actualRole === ROLES.ORG_ADMIN;
  const isBranchAdmin = actualRole === ROLES.BRANCH_ADMIN;
  const isWarehouseAdmin = actualRole === ROLES.WAREHOUSE_ADMIN;
  const isWarehouseUser = actualRole === ROLES.WAREHOUSE_USER;
  // Org-admin and branch-admin can edit purchases/inventory; org-users can only bill.
  const canManageStock = isAdmin || isBranchAdmin;

  const orgFromCache = useMemo(() => {
    if (!currentOrg) return null;
    const cached = findOrgById(currentOrg.id) || {};
    return { ...cached, ...currentOrg };
  }, [currentOrg, dataVersion]);

  const orgType = orgFromCache?.type || ORG_TYPES.PRODUCT;
  const branchesEnabled = useMemo(
    () => (orgFromCache ? getOrgSettings(orgFromCache.id, orgFromCache).branchesEnabled : false),
    [orgFromCache],
  );
  const moduleFlags = useMemo(() => {
    const on = (id) => (orgFromCache ? isModuleOn(orgFromCache, id) : false);
    return {
      zoneEnabled: on('zone'),
      warehouseEnabled: on('warehouse'),
      branchesEnabled,
      usersEnabled: on('users'),
      inventoryEnabled: on('inventory'),
      purchaseEnabled: (() => {
        const orgHasIt = on('inventory') && on('purchase');
        if (!orgHasIt) return false;
        if (actualRole === 'branch-admin' && currentUser?.branchId) {
          const branchList = branchesOf(orgFromCache?.id || '');
          const b = branchList.find(x => x.id === currentUser.branchId);
          if (b) {
            if (b.isMain) return true; // Main branch always has purchasing
            return !!b.purchaseEnabled;
          }
        }
        return true;
      })(),
      deliveryEnabled: on('delivery'),
      transportEnabled: on('transport'),
      ecommerceEnabled: on('ecommerce'),
      reportsEnabled: on('reports'),
      emergencyEnabled: on('emergency') && (on('transport') || on('warehouse') || on('branches')),
      feedbackEnabled: on('feedback'),
      appointmentsEnabled: on('appointments'),
      membershipsEnabled: on('memberships'),
      printerEnabled: on('printer'),
      scannerEnabled: on('scanner'),
      customerEnabled: on('customer'),
      offersDiscountsEnabled: on('offers_discounts'),
    };
  }, [orgFromCache, branchesEnabled, dataVersion, actualRole, currentUser]);

  const isT0 = useMemo(() => {
    if (!orgFromCache || !orgFromCache.modules) return true;
    const activeMods = Object.keys(orgFromCache.modules).filter(k => orgFromCache.modules[k] && k !== 'pos' && k !== 'catalog');
    return activeMods.length === 0;
  }, [orgFromCache]);

  const isT1 = useMemo(() => {
    if (!orgFromCache || !orgFromCache.modules) return false;
    if (isT0) return false;
    const { purchase, delivery, transport, warehouse } = orgFromCache.modules;
    return !purchase && !delivery && !transport && !warehouse;
  }, [orgFromCache, isT0]);

  const hideEmergAndDelivery = useMemo(() => {
    return !moduleFlags.emergencyEnabled;
  }, [moduleFlags]);

  // Org-admin spans every branch; branch-admin/org-user are locked to their branch.
  // With branches disabled the whole org operates on the single Main branch.
  const homeBranchId = useMemo(() => {
    if (!orgFromCache) return null;
    if (!branchesEnabled) return mainBranchOf(orgFromCache.id)?.id || null;
    return actualRole === ROLES.ORG_ADMIN ? null : (currentUser?.branchId || null);
  }, [orgFromCache, branchesEnabled, actualRole, currentUser]);

  const resolvedLocationId = useMemo(() => {
    if (homeBranchId) return homeBranchId;
    if (currentUser?.warehouseId) return currentUser.warehouseId;
    if (orgFromCache) {
      const scopes = resolveStockScopes(orgFromCache.id);
      if (scopes.inventoryScope === 'central') return CENTRAL_LOCATION;
      const branches = effectiveBranchesOf(orgFromCache.id);
      return branches[0]?.id || '';
    }
    return '';
  }, [homeBranchId, currentUser, orgFromCache]);

  // Reconnect the Bluetooth printer from wherever the page was refreshed.
  // This used to live only in PrinterTab and BillingTab, so refreshing on the
  // Dashboard, Inventory or Master Catalog reconnected nothing and the printer
  // stayed down until the cashier happened to open one of those two screens.
  // The dashboard wraps every org tab, so one attempt here covers them all.
  useEffect(() => {
    // Deliberately not gated on preferredPrinterMethod or moduleFlags. A user can pair a
    // printer without ever pressing "Set as Active Method", and gating on it
    // meant the supervisor never started for them. The supervisor itself stands
    // down when no printer is paired, so starting it costs nothing.

    import('../utils/printer')
      .then((printer) => {
        // Only if the user still wants the printer connected. Pressing
        // Disconnect clears that, and nothing here overrides it.
        if (printer.wantsBluetoothConnected()) printer.startBluetoothSupervisor();
      })
      // Silent: the printer may simply be off or out of range, and an error on
      // every page load would be worse than the problem.
      .catch(() => {});
  }, []);

  const alertedProductsRef = useRef(new globalThis.Map());

  // Global WebSocket-like Stock Notification System
  useEffect(() => {
    if (!orgFromCache || !moduleFlags.inventoryEnabled) return;
    if (!resolvedLocationId) return;

    const checkAlerts = () => {
      // Read both settings on every run so changing them in the Status tab takes
      // effect immediately, without a reload.
      if (localStorage.getItem('mitra_alerts_enabled') === 'off') return;
      const alertIntervalMs = Number(localStorage.getItem('mitra_alert_interval') || '5') * 60 * 1000;
      // Read stock inside the check: this runs on a timer, so reading it once
      // outside would leave every tick looking at page-load numbers.
      const activeStock = stockRowsOf(orgFromCache.id, resolvedLocationId, 'active');
      const now = Date.now();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const newAlerts = [];
      activeStock.forEach(s => {
        const minStock = Number(s.minStockThreshold) || 0;
        const numericQty = Number(s.qty) || 0;

        let diffDays = null;
        let isExpired = false;
        let isExpiringSoon = false;

        if (s.expiryDate) {
          const parts = s.expiryDate.split('-').map(Number);
          if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
            const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
            diffDays = Math.round((expDate - today) / (1000 * 60 * 60 * 24));
            if (diffDays < 0) {
              isExpired = true;
            } else if (diffDays <= 3) {
              isExpiringSoon = true;
            }
          }
        }

        const isOutOfStock = numericQty === 0;
        const isLowStock = minStock > 0 && numericQty <= minStock && numericQty > 0;

        if (isOutOfStock || isLowStock || isExpired || isExpiringSoon) {
          const p = catalogProductsOf(orgFromCache.id).find(prod => prod.id === s.productId);
          if (p) {
            const alertKey = `stock-${s.id}`;
            const lastAlert = alertedProductsRef.current.get(alertKey);
            const lastAlertTime = typeof lastAlert === 'number' ? lastAlert : (lastAlert?.time || 0);
            const lastAlertQty = typeof lastAlert === 'object' ? lastAlert.qty : -1;
            const lastAlertExpired = typeof lastAlert === 'object' ? lastAlert.expired : false;

            const intervalPassed = now - lastAlertTime >= alertIntervalMs;
            const newlyOut = isOutOfStock && lastAlertQty !== 0;
            const newlyExpired = isExpired && !lastAlertExpired;

            if (intervalPassed || newlyOut || newlyExpired) {
              const v = p.variants?.find(v => v.id === s.variantId);
              const variantName = v && v.label !== 'Default' ? ` (${v.label})` : '';

              if (isExpired) {
                newAlerts.push(`${p.name}${variantName} has EXPIRED (${s.expiryDate})!`);
              } else if (isOutOfStock) {
                newAlerts.push(`${p.name}${variantName} is out of stock!`);
              } else if (isExpiringSoon) {
                const daysLeftStr = diffDays === 0 ? 'today' : `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
                newAlerts.push(`${p.name}${variantName} is expiring ${daysLeftStr} (${s.expiryDate})!`);
              } else if (isLowStock) {
                newAlerts.push(`${p.name}${variantName} is running low (${numericQty} remaining)`);
              }

              alertedProductsRef.current.set(alertKey, {
                time: now,
                qty: numericQty,
                expired: isExpired,
                expiringSoon: isExpiringSoon,
              });
            }
          }
        } else {
          alertedProductsRef.current.delete(`stock-${s.id}`);
        }
      });

      if (newAlerts.length > 0) {
        const hasUrgent = newAlerts.some(msg =>
          msg.toLowerCase().includes('out of stock') ||
          msg.toLowerCase().includes('no stock') ||
          msg.toLowerCase().includes('expired')
        );
        const alertType = hasUrgent ? 'nostock' : 'warning';
        if (newAlerts.length === 1) {
          showNotification(`Live Update: ${newAlerts[0]}`, alertType);
        } else {
          showNotification(`Live Update: ${newAlerts.length} products have stock & expiry alerts!`, alertType);
        }

        try {
          const bell = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
          bell.volume = 0.5;
          bell.play().catch(() => {
            // Browser might block audio if there was no user interaction yet.
            console.warn("Audio playback blocked by browser.");
          });
        } catch {
          // Ignore audio errors
        }
      }
    };

    // Check immediately on data change, then keep checking on a timer. Without
    // the timer nothing ever re-ran this, so the Alert Interval only behaved as
    // a "do not repeat within X" limit and a toast could only appear on refresh.
    // The tick is deliberately shorter than the interval: it is the per-product
    // cooldown above that enforces the user's 1/5/15/… minute choice, so a
    // product that has just run out is noticed promptly instead of waiting a
    // whole interval.
    checkAlerts();
    const ticker = setInterval(checkAlerts, ALERT_TICK_MS);
    return () => clearInterval(ticker);

  }, [dataVersion, orgFromCache, resolvedLocationId, homeBranchId, currentUser, moduleFlags.inventoryEnabled, catalogProductsOf, stockRowsOf, showNotification]);

  const notifications = useMemo(() => {
    if (!orgFromCache || !moduleFlags.inventoryEnabled) return [];
    if (!resolvedLocationId) return [];

    const products = catalogProductsOf(orgFromCache.id);
    const activeStock = stockRowsOf(orgFromCache.id, resolvedLocationId, 'active');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const list = [];
    activeStock.forEach(s => {
      const minStock = Number(s.minStockThreshold) || 0;
      const numericQty = Number(s.qty) || 0;

      let diffDays = null;
      let isExpired = false;
      let isExpiringSoon = false;

      if (s.expiryDate) {
        const parts = s.expiryDate.split('-').map(Number);
        if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
          const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
          diffDays = Math.round((expDate - today) / (1000 * 60 * 60 * 24));
          if (diffDays < 0) {
            isExpired = true;
          } else if (diffDays <= 3) {
            isExpiringSoon = true;
          }
        }
      }

      const p = products.find(prod => prod.id === s.productId);
      if (p) {
        const v = p.variants?.find(v => v.id === s.variantId);
        const variantName = v && v.label !== 'Default' ? ` - ${v.label}` : '';

        if (isExpired) {
          list.push({
            id: `alert-exp-${s.id}`,
            text: `🛑 EXPIRED: ${p.name}${variantName} expired on ${s.expiryDate}`,
            qty: s.qty
          });
        } else if (numericQty === 0) {
          list.push({
            id: `alert-out-${s.id}`,
            text: `${p.name}${variantName} is out of stock!`,
            qty: s.qty
          });
        } else if (isExpiringSoon) {
          const daysLeftStr = diffDays === 0 ? 'today' : `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
          list.push({
            id: `alert-soon-${s.id}`,
            text: `⚠️ EXPIRING SOON: ${p.name}${variantName} expires ${daysLeftStr} (${s.expiryDate})`,
            qty: s.qty
          });
        } else if (minStock > 0 && numericQty <= minStock) {
          list.push({
            id: `alert-low-${s.id}`,
            text: `${p.name}${variantName} is running low (${numericQty} left).`,
            qty: s.qty
          });
        }
      }
    });

    if (!isT0 && !isT1) {
      const emergencies = emergencyRequestsOf(orgFromCache.id).filter(r => r.status === 'open');
      emergencies.forEach(req => {
        const p = products.find(prod => prod.id === req.productId);
        if (p) {
          const v = p.variants?.find(v => v.id === req.variantId);
          const variantName = v && v.label !== 'Default' ? ` - ${v.label}` : '';
          const branchName = locationLabelOf(req.requestingBranchId);
          list.push({
            id: `er-${req.id}`,
            text: `🚨 EMERGENCY: ${branchName} needs ${req.qty} ${p.name}${variantName}`,
            qty: req.qty
          });
        }
      });
    }

    return list;
  }, [dataVersion, orgFromCache, resolvedLocationId, homeBranchId, currentUser, moduleFlags.inventoryEnabled, isT0, isT1]);

  // What this user may do, fetched once per session. The sidebar is built from
  // the answer, so it asks the server rather than working it out from a role
  // name — the menu and the middleware then cannot disagree.
  useEffect(() => { loadMyAccess(); }, []);

  // dataVersion is in the list because the permissions arriving is a store
  // change like any other: without it the sidebar would stay on the optimistic
  // "everything" it draws while the answer is still in flight.
  const navItems = useMemo(
    () => buildNav(actualRole, orgType, moduleFlags, isT0, isT1),
    [actualRole, orgType, moduleFlags, isT0, isT1, dataVersion],
  );
  const flatItems = useMemo(() => flatten(navItems), [navItems]);

  // Ensure activeTab is valid for this role/org.
  const validIds = useMemo(() => flatItems.filter((i) => !i.children).map((i) => i.id), [flatItems]);
  const effectiveTab = (isStoreLoading || validIds.includes(activeTab)) ? activeTab : 'dashboard';

  // The screens slide, and the direction is decided once per tab change.
  //
  // It used to be worked out on every render, from a ref that only updated
  // afterwards in an effect — the reason for the `react-hooks/refs` suppression
  // that sat on it. That is not a value that holds still: this screen re-renders
  // on every websocket message and every data load, so the direction could flip
  // while a slide was still running. AnimatePresence would then retarget both
  // the leaving and the arriving panel to the opposite side, they chased a
  // moving target, and could come to rest off-screen inside a container that
  // hides its overflow — a page that looks stuck. Master Catalog showed it most,
  // because opening one of those tabs starts a catalogue load and so causes
  // exactly the extra renders that flipped it.
  //
  // Holding it in state fixes that: the direction is computed at the moment the
  // tab actually changes and then stays put, so nothing that happens during the
  // animation can move the target. Setting state during render is React's own
  // pattern for a value derived from a changing prop — the re-render happens
  // before anything is shown, so there is no extra frame.
  const [slide, setSlide] = useState({ tab: effectiveTab, direction: 1 });

  if (slide.tab !== effectiveTab) {
    const from = validIds.indexOf(slide.tab);
    const to = validIds.indexOf(effectiveTab);
    // A tab missing from the list gives -1, which would read as "moved left"
    // for anything. Forward is the sane default when we cannot tell.
    const direction = from === -1 || to === -1 ? 1 : (to > from ? 1 : -1);
    setSlide({ tab: effectiveTab, direction });
  }

  const direction = slide.direction;

  const tabVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? '-100%' : '100%', y: 0 }),
    center: { opacity: 1, x: 0, y: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? '100%' : '-100%', y: 0 }),
  };

  const titleFor = (tab) => {
    const item = flatItems.find((i) => i.id === tab && !i.children);
    return item?.label || 'Dashboard';
  };
  const onLogout = () => { if (showNotification) showNotification('Logged out successfully!', 'success'); handleLogout(); };
  const isSidebar = navMode === 'sidebar';

  // Pass the organization, not its id. The loader reads `orgType` and `modules`
  // off what it is given to decide which endpoints to ask for; handed a bare id
  // it saw neither, so product and service data loaded for every shop by
  // accident while delivery, feedback and emergency loaded for none. On a cold
  // start the cache is the only place this object exists — the loader is what
  // fills the organizations list, so it cannot look itself up.
  const loadData = async (orgRef, background = false) => {
    if (orgRef) {
      await loadAllTenantData(orgRef, background);
    }
  };

  const [, setWsStatus] = useState('disconnected');
  const hasInitiallyConnectedRef = useRef(false);
  const wasDisconnectedRef = useRef(false);

  useEffect(() => {
    loadData(currentOrg);

    // The shop no longer needs passing: the socket is authenticated with the
    // access token, and the server reads the shop from it.
    wsManager.connect();

    const unsubscribe = wsManager.subscribe((msg) => {
      if (msg.type === 'STATUS') {
        setWsStatus(msg.status);
        if (msg.status === 'connected') {
          if (!hasInitiallyConnectedRef.current) {
            hasInitiallyConnectedRef.current = true;
            wasDisconnectedRef.current = false;
          } else if (wasDisconnectedRef.current && currentOrg?.id) {
            wasDisconnectedRef.current = false;
            loadData(currentOrg, true);
          }
        } else if (msg.status === 'disconnected') {
          if (hasInitiallyConnectedRef.current) {
            wasDisconnectedRef.current = true;
          }
        }
      } else {
        // Only trigger data refresh if the message actually belongs to this Org
        if (currentOrg?.id && msg.orgId === currentOrg.id) {
          loadData(currentOrg, true);
        }
      }
    });

    return () => {
      unsubscribe();
      // We don't disconnect the socket here so it stays alive across any internal unmounts
    };
  }, [currentOrg?.id]);

  const [printableBill, setPrintableBill] = useState(null);

  if (!currentOrg) return null;

  return (
    <div className="w-full flex overflow-hidden" style={{ height: '100dvh', background: 'var(--theme-secondary)' }}>
      {isSidebar && effectiveTab !== 'kiosk-queue' && (
        <Sidebar
          org={orgFromCache}
          items={navItems}
          activeTab={effectiveTab}
          setActiveTab={setActiveTab}
          onSwitchToFloating={() => setNavMode('floating')}
        />
      )}

      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {effectiveTab !== 'kiosk-queue' && (
        <TopBar
          org={orgFromCache}
          meta={titleFor(effectiveTab)}
          user={currentUser}
          showBrand={!isSidebar}
          notifications={notifications}
          onProfileClick={() => setActiveTab('profile')}
          onLogout={onLogout}
          onNotificationsClick={() => {
            console.log("OrgDashboard: navigating to inventory-active");
            setActiveTab('inventory-active');
          }}
          rightSlot={(() => {
            const d = daysUntilExpiry(orgFromCache);
            if (d === null) return null;
            const tone = d <= 7 ? { bg: 'rgba(244,63,94,0.10)', fg: '#f43f5e' }
              : d <= 30 ? { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' }
                : { bg: 'rgba(10,185,129,0.10)', fg: '#047857' };
            return (
              <div
                title={`Plan expires ${new Date(orgFromCache.planExpiresAt).toLocaleDateString()}`}
                className="hidden sm:flex items-center gap-1.5 px-2 text-[11px] font-extrabold uppercase tracking-[0.15em]"
                style={{ color: tone.fg }}
              >
                {d} day{d === 1 ? '' : 's'} left
              </div>
            );
          })()}
        />
        )}
        <main className="flex-1 min-h-0 overflow-hidden flex flex-col relative">
          {/* popLayout, not wait.

              `wait` runs the two halves in sequence: slide the old panel out,
              unmount it, mount the new screen, then slide that in. The middle
              step is React building a whole screen — Products and its tables are
              not cheap — and it blocks the main thread with nothing on display.
              That is the pause: the page slides away, stops dead for as long as
              the next screen takes to build, and only then starts moving again.

              `popLayout` takes the leaving panel out of the layout flow so the
              arriving one takes its place at once and both animate together.
              The new screen is built while the old one is still sliding, and
              that slide is a transform, so it keeps going on the compositor even
              while the main thread is busy. No gap left to notice. */}
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={effectiveTab}
              custom={direction}
              variants={tabVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className={`app-content flex-1 min-h-0 w-full h-full flex flex-col overflow-x-hidden ${
                effectiveTab === 'kiosk-queue' 
                  ? 'overflow-hidden' 
                  : (effectiveTab === 'sales' || effectiveTab === 'customer-management' || effectiveTab === 'notifications' ? 'overflow-hidden p-3 md:p-4 lg:p-6' : 'p-3 md:p-4 lg:p-6 overflow-y-auto custom-scrollbar')
              }`}
            >
              {isStoreLoading && (branchesOf(currentOrg?.id)?.length || 0) === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center min-h-[400px]">
                  <div className="w-10 h-10 border-4 border-slate-200 border-t-[var(--theme-primary)] rounded-full animate-spin"></div>
                  <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Loading Data...</p>
                </div>
              ) : (
                <div className={
                  (effectiveTab === 'kiosk-queue' || effectiveTab === 'staff-schedule')
                      ? "w-full h-full flex-1 flex flex-col min-h-0"
                      : (effectiveTab === 'sales' || effectiveTab === 'customer-management')
                      ? "max-w-7xl mx-auto w-full h-full flex-1 flex flex-col min-h-0"
                      : effectiveTab === 'notifications'
                      ? "w-full h-full flex-1 flex flex-col min-h-0"
                      : "max-w-7xl mx-auto w-full pb-10 mb-auto"
                }>
                  {effectiveTab === 'dashboard' && (
                    (isWarehouseAdmin || isWarehouseUser) && currentUser?.warehouseId ? (
                      <WarehouseDashboard warehouseId={currentUser.warehouseId} org={currentOrg} user={currentUser} showNotification={showNotification} />
                    ) : (
                      <OrgAdminDashboard org={currentOrg} user={currentUser} role={actualRole} dataVersion={dataVersion} showNotification={showNotification} />
                    )
                  )}
                  {effectiveTab === 'zones' && isAdmin && (
                    <ZonesTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'warehouses' && isAdmin && (
                    <WarehousesTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'org-wh-purchases' && isAdmin && (
                    <OrgWhPurchasesTab org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'org-wh-stock' && isAdmin && (
                    <OrgWhStockTab org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'org-wh-received' && isAdmin && (
                    <OrgWhReceivedTab org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'org-wh-transport' && isAdmin && (
                    <OrgWhTransportTab org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'branches' && isAdmin && (
                    <BranchesTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'users' && (isAdmin || isBranchAdmin) && (
                    <OrgUsersList org={currentOrg} showNotification={showNotification} branchId={isAdmin || !branchesEnabled ? null : homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'roles-acl' && (
                    <RolesAclTab org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'purchases' && (
                    <PurchasesTab org={currentOrg} showNotification={showNotification} canEdit={canManageStock} lockedBranchId={homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'inventory-pending' && (
                    <InventoryPendingTab org={currentOrg} showNotification={showNotification} canEdit={canManageStock} lockedBranchId={homeBranchId} dataVersion={dataVersion} isT0={isT0} isT1={isT1} />
                  )}
                  {effectiveTab === 'inventory-active' && (
                    <InventoryActiveTab org={currentOrg} showNotification={showNotification} canEdit={canManageStock} lockedBranchId={homeBranchId} dataVersion={dataVersion} isT1={isT1} hideEmergAndDelivery={hideEmergAndDelivery} />
                  )}
                  {effectiveTab === 'barcode-labels' && (
                    <BarcodeLabelsTab org={currentOrg} showNotification={showNotification} onNavigate={setActiveTab} />
                  )}
                  {effectiveTab === 'inventory-history' && (
                    <InventoryHistoryTab org={currentOrg} showNotification={showNotification} lockedBranchId={homeBranchId} />
                  )}
                  {effectiveTab === 'inventory-status' && (
                    <InventoryStatusTab org={currentOrg} branchId={resolvedLocationId} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'billing' && (
                    <BillingTab
                      org={orgFromCache}
                      user={currentUser}
                      showNotification={showNotification}
                      onBillPrinted={setPrintableBill}
                      lockedBranchId={homeBranchId}
                      dataVersion={dataVersion}
                      initialPrefill={billingPrefill}
                      clearPrefill={() => setBillingPrefill(null)}
                    />
                  )}
                  {effectiveTab === 'sales' && (
                    <SalesLedgerTab org={currentOrg} user={currentUser} role={actualRole} branchId={homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'appointments' && (
                    <AppointmentsTab
                      org={currentOrg}
                      showNotification={showNotification}
                      homeBranchId={homeBranchId}
                      dataVersion={dataVersion}
                      onBillAppointment={({ apt, service, staff }) => {
                        setBillingPrefill({
                          customerName: apt.customerName,
                          customerPhone: apt.phone || '',
                          serviceId: service?.id,
                          staffId: staff?.id,
                          orderType: 'booking'
                        });
                        setActiveTab('billing');
                      }}
                    />
                  )}
                  {effectiveTab === 'my-bookings' && (
                    <MyBookingsTab org={currentOrg} currentUser={currentUser} />
                  )}
                  {effectiveTab === 'staff-schedule' && (
                    <StaffScheduleTab org={currentOrg} showNotification={showNotification} homeBranchId={homeBranchId} />
                  )}
                  {effectiveTab === 'notifications' && (
                    <NotificationSettingsTab org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'kiosk-queue' && (
                    <KioskQueueTab org={currentOrg} onClose={() => setActiveTab('dashboard')} />
                  )}
                  {effectiveTab === 'customer-packages' && (
                    <MembershipsTab org={currentOrg} showNotification={showNotification} homeBranchId={homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-packages' && (
                    <MasterPackagesTab org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'offers-discounts' && (
                    <OffersDiscountsTab org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'feedback' && (
                    <CustomerFeedbackTab org={currentOrg} showNotification={showNotification} homeBranchId={homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'delivery' && (
                    <DeliveryTab org={currentOrg} role={actualRole} showNotification={showNotification} lockedBranchId={homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'transport' && (
                    <TransportTab
                      org={currentOrg}
                      showNotification={showNotification}
                      lockedBranchId={homeBranchId}
                      direction="all"
                      dataVersion={dataVersion}
                    />
                  )}
                  {effectiveTab === 'emergency' && (
                    <EmergencyRequestsView
                      org={currentOrg}
                      accent={currentOrg?.theme?.primary || '#3b82f6'}
                      showNotification={showNotification}
                      lockedBranchId={homeBranchId || currentUser?.warehouseId}
                    />
                  )}
                  {effectiveTab === 'ecommerce' && (
                    <EcommerceTab org={currentOrg} role={actualRole} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'device-printer' && (
                    <PrinterTab org={currentOrg} role={actualRole} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'device-scanner' && (
                    <ScannerTab org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'mybranch' && isBranchAdmin && homeBranchId && (
                    <MyBranchTab branchId={homeBranchId} org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'branch-stocks' && isBranchAdmin && homeBranchId && (
                    <BranchStocksTab branchId={homeBranchId} org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'branch-received-details' && isBranchAdmin && homeBranchId && (
                    <BranchReceivedDetailsTab branchId={homeBranchId} org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'branch-transport' && isBranchAdmin && homeBranchId && (
                    <TransportTab
                      org={currentOrg}
                      showNotification={showNotification}
                      lockedBranchId={homeBranchId}
                      dataVersion={dataVersion}
                    />
                  )}

                  {effectiveTab === 'wh-purchases' && (
                    <WhPurchasesTab warehouseId={currentUser.warehouseId} org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'wh-inventory-history' && (
                    <WhInventoryHistoryTab warehouseId={currentUser.warehouseId} org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'wh-sourcing' && isWarehouseAdmin && currentUser?.warehouseId && (
                    <WhSourcingTab warehouseId={currentUser.warehouseId} org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'wh-stock' && (isWarehouseAdmin || isWarehouseUser) && currentUser?.warehouseId && (
                    <WhStockTab warehouseId={currentUser.warehouseId} org={currentOrg} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'wh-pending-stock' && (isWarehouseAdmin || isWarehouseUser) && currentUser?.warehouseId && (
                    <WhPendingStockTab warehouseId={currentUser.warehouseId} org={currentOrg} showNotification={showNotification} canEdit={isWarehouseAdmin} />
                  )}
                  {effectiveTab === 'wh-inventory-status' && isWarehouseAdmin && currentUser?.warehouseId && (
                    <WhInventoryStatusTab warehouseId={currentUser.warehouseId} org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'wh-transport' && (isWarehouseAdmin || isWarehouseUser) && currentUser?.warehouseId && (
                    <TransportTab
                      org={currentOrg}
                      showNotification={showNotification}
                      lockedBranchId={currentUser.warehouseId}
                      direction="all"
                      dataVersion={dataVersion}
                    />
                  )}
                  {effectiveTab === 'master-p-category' && (
                    <PCategoryTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-p-subcategory' && (
                    <PSubCategoryTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-products' && (
                    <ProductsTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-s-category' && (
                    <SCategoryTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-s-subcategory' && (
                    <SSubCategoryTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-services' && (
                    <ServicesTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-units' && (
                    <UnitsTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-gst' && (
                    <GstTab org={currentOrg} showNotification={showNotification} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'master-suppliers' && (
                    <SuppliersTab org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'master-delivery-fees' && (
                    <DeliveryFeesTab
                      currentOrg={currentOrg}
                      accent={currentOrg?.theme?.primary || '#0a5132'}
                      showNotification={showNotification}
                    />
                  )}
                  {effectiveTab === 'master-shifts' && (
                    <MasterShiftsTab org={currentOrg} showNotification={showNotification} />
                  )}
                  {effectiveTab === 'master-validation' && (
                    <OrgValidationSettingsTab />
                  )}
                  {effectiveTab === 'report-sales' && (
                    <SalesReportTab org={currentOrg} user={currentUser} role={actualRole} branchId={homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'report-purchase' && (
                    <PurchaseReportTab org={currentOrg} branchId={homeBranchId} dataVersion={dataVersion} />
                  )}
                  {effectiveTab === 'subscription' && isAdmin && (
                    <SubscriptionTab
                      org={orgFromCache || currentOrg}
                      showNotification={showNotification}
                      onRenewed={(updated) => onUpdateOrg && onUpdateOrg(updated)}
                    />
                  )}
                  {effectiveTab === 'customer-management' && (
                    <CustomersTab org={currentOrg} showNotification={showNotification} />
                  )}

                  {effectiveTab === 'profile' && (
                    <OrgAdminProfile org={orgFromCache || currentOrg} user={currentUser} showNotification={showNotification} onUpdateUser={onUpdateUser} onUpdateOrg={onUpdateOrg} isT0={isT0} isT1={isT1} />
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {!isSidebar && (
        <FloatingNav
          items={navItems}
          activeTab={effectiveTab}
          setActiveTab={setActiveTab}
          onSwitchToSidebar={() => setNavMode('sidebar')}
        />
      )}

      <AnimatePresence>
        {printableBill && (
          <PrintBillModal bill={printableBill} org={currentOrg} onClose={() => setPrintableBill(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

OrgDashboard.propTypes = {
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  navMode: PropTypes.string.isRequired,
  setNavMode: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  currentOrg: PropTypes.object,
  role: PropTypes.string,
  onUpdateUser: PropTypes.func,
  onUpdateOrg: PropTypes.func,
};


